import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { MultifactorAuthState, MultifactorAuth } from './types';
import * as types from './mutation-types';

// 全てのエラーをクリアする
const resetAllErrors = (state: MultifactorAuthState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorTitle = undefined;
    searchState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const switchDevicesState = state.switchDevices;
    switchDevicesState.error = false;
    switchDevicesState.errorTitle = undefined;
    switchDevicesState.errorDetails = undefined;

    const resetTimeCorrectionsState = state.resetTimeCorrections;
    resetTimeCorrectionsState.error = false;
    resetTimeCorrectionsState.errorTitle = undefined;
    resetTimeCorrectionsState.errorDetails = undefined;

    const removalUsersState = state.removalUsers;
    removalUsersState.error = false;
    removalUsersState.errorTitle = undefined;
    removalUsersState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorTitle = undefined;
    csvState.errorDetails = undefined;
};

export const mutations: MutationTree<MultifactorAuthState> = {
    // 多要素認証デバイス検索系
    [types.SEARCH_START](state) {
        resetAllErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; devices: Array<MultifactorAuth> }) {
        state.count = payload.count;
        state.lists = payload.devices;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const searchState = state.search;
        searchState.errorTitle = payload.errorTitle;
        searchState.errorDetails = payload.errorDetails;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 多要素認証デバイス(ハードデバイス)作成系
    [types.NEW_HARD_DEVICES](state) {
        const createHardState = state.hard;
        createHardState.error = false;
        createHardState.errorTitle = undefined;
        createHardState.errorDetails = undefined;
        createHardState.isCreated = false;
    },

    [types.CREATE_HARD_DEVICES_START](state) {
        const createHardState = state.hard;
        createHardState.error = false;
        createHardState.errorTitle = undefined;
        createHardState.errorDetails = undefined;
        createHardState.isProcessing = true;
    },

    [types.CREATE_HARD_DEVICES_SUCCESS](state) {
        const createHardState = state.hard;
        createHardState.isProcessing = false;
        createHardState.isCreated = true;
    },

    [types.CREATE_HARD_DEVICES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createHardState = state.hard;
        createHardState.errorTitle = payload.errorTitle;
        createHardState.errorDetails = payload.errorDetails;
        createHardState.error = true;
        createHardState.isProcessing = false;
    },

    // 多要素認証デバイス詳細情報参照系
    [types.SHOW_START](state) {
        resetAllErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: MultifactorAuth) {
        state.details = payload;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // デバイスの多要素認証有効／無効更新系
    [types.SWITCH_DEVICES_START](state) {
        resetAllErrors(state);
        const switchDevicesState = state.switchDevices;
        switchDevicesState.isProcessing = true;
    },

    [types.SWITCH_DEVICES_SUCCESS](state) {
        const switchDevicesState = state.switchDevices;
        switchDevicesState.isProcessing = false;
    },

    [types.SWITCH_DEVICES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const switchDevicesState = state.switchDevices;
        switchDevicesState.errorTitle = payload.errorTitle;
        switchDevicesState.errorDetails = payload.errorDetails;
        switchDevicesState.error = true;
        switchDevicesState.isProcessing = false;
    },

    [types.RESET_TIME_CORRECTIONS_START](state) {
        resetAllErrors(state);
        const resetTimeCorrectionsState = state.resetTimeCorrections;
        resetTimeCorrectionsState.isProcessing = true;
    },

    [types.RESET_TIME_CORRECTIONS_SUCCESS](state) {
        const resetTimeCorrectionsState = state.resetTimeCorrections;
        resetTimeCorrectionsState.isProcessing = false;
    },

    [types.RESET_TIME_CORRECTIONS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const resetTimeCorrectionsState = state.resetTimeCorrections;
        resetTimeCorrectionsState.errorTitle = payload.errorTitle;
        resetTimeCorrectionsState.errorDetails = payload.errorDetails;
        resetTimeCorrectionsState.error = true;
        resetTimeCorrectionsState.isProcessing = false;
    },

    [types.REMOVAL_USERS_START](state) {
        resetAllErrors(state);
        const removalUsersState = state.removalUsers;
        removalUsersState.isProcessing = true;
    },

    [types.REMOVAL_USERS_SUCCESS](state) {
        const removalUsersState = state.removalUsers;
        removalUsersState.isProcessing = false;
    },

    [types.REMOVAL_USERS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removalUsersState = state.removalUsers;
        removalUsersState.errorTitle = payload.errorTitle;
        removalUsersState.errorDetails = payload.errorDetails;
        removalUsersState.error = true;
        removalUsersState.isProcessing = false;
    },

    // 多要素認証デバイス削除系
    [types.DESTROY_START](state) {
        resetAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetAllErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const csvState = state.csv;
        csvState.errorTitle = payload.errorTitle;
        csvState.errorDetails = payload.errorDetails;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createHardState = state.hard;
        createHardState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const switchDevicesState = state.switchDevices;
        switchDevicesState.isProcessing = false;

        const resetTimeCorrectionsState = state.resetTimeCorrections;
        resetTimeCorrectionsState.isProcessing = false;

        const removalUsersState = state.removalUsers;
        removalUsersState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        resetAllErrors(state);
        state.lists = undefined;
        state.count = 0;
        state.details = undefined;
    },
};
